import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useEnhancedFinancialData } from '@/hooks/useEnhancedFinancialData';
import { Check, X, Trash2, Download, Plus, Search, Filter, TrendingUp, TrendingDown, Users, Clock, RefreshCw } from 'lucide-react';
import { formatArmenianDateTime } from '@/utils/dateFormatting';

export const EnhancedFinancialSection = () => {
  const {
    records,
    statistics,
    loading,
    filters,
    setFilters,
    updateRecordStatus,
    bulkUpdateStatus,
    deleteRecord,
    addManualRecord,
    formatAmount,
    getTypeLabel,
    getStatusLabel,
    fetchEnhancedFinancialRecords,
    fetchFinancialStatistics,
    fetchUserBalance
  } = useEnhancedFinancialData();

  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchEnhancedFinancialRecords(),
        fetchFinancialStatistics(),
        fetchUserBalance()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const [newRecord, setNewRecord] = useState({
    user_id: '',
    amount: '',
    type: 'tuition_debt',
    description: ''
  });

  const handleSelectRecord = (recordId: string) => {
    setSelectedRecords(prev => 
      prev.includes(recordId) 
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(r => r.id));
    }
  };

  const handleAddRecord = async () => {
    if (newRecord.user_id && newRecord.amount && newRecord.description) {
      const success = await addManualRecord({
        user_id: newRecord.user_id,
        amount: parseFloat(newRecord.amount),
        type: newRecord.type,
        description: newRecord.description
      });

      if (success) {
        setIsAddDialogOpen(false);
        setNewRecord({ user_id: '', amount: '', type: 'tuition_debt', description: '' });
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <Check className="h-4 w-4 text-primary" />;
      case 'rejected': return <X className="h-4 w-4 text-destructive" />;
      case 'pending': return <Clock className="h-4 w-4 text-primary" />;
      default: return null;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'tuition_debt': return 'bg-red-500/20 text-destructive border-red-500/30';
      case 'tuition_payment': return 'bg-primary/10 text-primary border-green-500/30';
      case 'salary_earned': return 'bg-blue-500/20 text-muted-foreground border-blue-500/30';
      case 'salary_payment': return 'bg-purple-500/20 text-muted-foreground border-purple-500/30';
      default: return 'bg-muted/50 text-muted-foreground border-border';
    }
  };

  if (loading) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Բեռնվում է...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Overview */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-card/80 border-border/70">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Սպասող աշխատավարձ</span>
              </div>
              <div className="text-lg font-bold text-muted-foreground">
                {formatAmount(statistics.total_pending_salaries)}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/80 border-border/70">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Վճարված աշխատավարձ</span>
              </div>
              <div className="text-lg font-bold text-muted-foreground">
                {formatAmount(statistics.total_approved_salaries)}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/80 border-border/70">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">Սպասող վարձավճար</span>
              </div>
              <div className="text-lg font-bold text-destructive">
                {formatAmount(statistics.total_pending_tuition)}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/80 border-border/70">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Վճարված վարձավճար</span>
              </div>
              <div className="text-lg font-bold text-muted-foreground">
                {formatAmount(statistics.total_approved_tuition)}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/80 border-border/70">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Դասախոսներ</span>
              </div>
              <div className="text-lg font-bold text-muted-foreground">
                {statistics.lecturers_awaiting_payment}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/80 border-border/70">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Պարտքով ուսանողներ</span>
              </div>
              <div className="text-lg font-bold text-muted-foreground">
                {statistics.students_with_debt}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-card/50 border-border/50">
        <CardHeader>
          <CardTitle className="text-muted-foreground">Ֆինանսական կառավարում</CardTitle>
          <CardDescription className="text-muted-foreground">
            Աշխատավարձի և վարձավճարի կառավարման նոր համակարգ
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters and Actions */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Փնտրել..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="min-h-11 w-full border-border bg-muted/50 sm:w-64"
                />
              </div>

              <Select
                value={filters.type}
                onValueChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className="min-h-11 w-full border-border bg-muted/50 sm:w-48">
                  <SelectValue placeholder="Տեսակ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Բոլոր տեսակները</SelectItem>
                  <SelectItem value="tuition_debt">Վարձավճարի պարտք</SelectItem>
                  <SelectItem value="tuition_payment">Վարձավճարի վճարում</SelectItem>
                  <SelectItem value="salary_earned">Աշխատավարձ</SelectItem>
                  <SelectItem value="salary_payment">Աշխատավարձի վճարում</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="min-h-11 w-full border-border bg-muted/50 sm:w-48">
                  <SelectValue placeholder="Կարգավիճակ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Բոլոր կարգավիճակները</SelectItem>
                  <SelectItem value="pending">Սպասման մեջ</SelectItem>
                  <SelectItem value="approved">Հաստատված</SelectItem>
                  <SelectItem value="rejected">Մերժված</SelectItem>
                </SelectContent>
              </Select>

              <Button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="min-h-11 w-full bg-primary hover:bg-primary/90 sm:w-auto"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Թարմացնել
              </Button>

              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="min-h-11 w-full bg-primary hover:bg-primary/90 sm:w-auto">
                    <Plus className="h-4 w-4 mr-2" />
                    Ավելացնել գրանցում
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-[calc(100vw-2rem)] border-border bg-card sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-muted-foreground">Նոր ֆինանսական գրանցում</DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                      Ցավելացնել ֆինանսական գրանցում ձեռքով
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="user_id">Օգտատիրոջ ID</Label>
                      <Input
                        id="user_id"
                        value={newRecord.user_id}
                        onChange={(e) => setNewRecord(prev => ({ ...prev, user_id: e.target.value }))}
                        className="bg-muted border-border"
                      />
                    </div>
                    <div>
                      <Label htmlFor="amount">Գումար</Label>
                      <Input
                        id="amount"
                        type="number"
                        value={newRecord.amount}
                        onChange={(e) => setNewRecord(prev => ({ ...prev, amount: e.target.value }))}
                        className="bg-muted border-border"
                      />
                    </div>
                    <div>
                      <Label htmlFor="type">Տեսակ</Label>
                      <Select
                        value={newRecord.type}
                        onValueChange={(value) => setNewRecord(prev => ({ ...prev, type: value }))}
                      >
                        <SelectTrigger className="bg-muted border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tuition_debt">Վարձավճարի պարտք</SelectItem>
                          <SelectItem value="tuition_payment">Վարձավճարի վճարում</SelectItem>
                          <SelectItem value="salary_earned">Աշխատավարձ</SelectItem>
                          <SelectItem value="salary_payment">Աշխատավարձի վճարում</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="description">Նկարագրություն</Label>
                      <Textarea
                        id="description"
                        value={newRecord.description}
                        onChange={(e) => setNewRecord(prev => ({ ...prev, description: e.target.value }))}
                        className="bg-muted border-border"
                      />
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button onClick={handleAddRecord} className="min-h-11 w-full bg-primary hover:bg-primary/90 sm:w-auto">
                        Ավելացնել
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setIsAddDialogOpen(false)}
                        className="min-h-11 w-full border-border sm:w-auto"
                      >
                        Չեղարկել
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Bulk Actions */}
            {selectedRecords.length > 0 && (
              <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/50 p-3 sm:flex-row sm:items-center">
                <span className="text-muted-foreground">{selectedRecords.length} ընտրված</span>
                <Button
                  size="sm"
                  onClick={() => bulkUpdateStatus(selectedRecords, 'approved')}
                  className="min-h-11 w-full bg-primary hover:bg-primary/90 sm:w-auto"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Հաստատել բոլորը
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="min-h-11 w-full sm:w-auto"
                  onClick={() => bulkUpdateStatus(selectedRecords, 'rejected')}
                >
                  <X className="h-4 w-4 mr-1" />
                  Մերժել բոլորը
                </Button>
              </div>
            )}
          </div>

          {/* Financial Records Table */}
          <div className="overflow-x-auto rounded-lg border border-border/50">
            <Table className="min-w-[980px]">
              <TableHeader className="bg-card/50">
                <TableRow className="border-border/50 hover:bg-muted/50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedRecords.length === records.length && records.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="text-muted-foreground">Օգտատեր</TableHead>
                  <TableHead className="text-muted-foreground">Տեսակ</TableHead>
                  <TableHead className="text-muted-foreground">Գումար</TableHead>
                  <TableHead className="text-muted-foreground">Նկարագրություն</TableHead>
                  <TableHead className="text-muted-foreground">Փուլ/Խումբ</TableHead>
                  <TableHead className="text-muted-foreground">Կարգավիճակ</TableHead>
                  <TableHead className="text-muted-foreground">Ամսաթիվ</TableHead>
                  <TableHead className="text-muted-foreground">Գործողություններ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      Ֆինանսական գրանցումներ չկան
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow
                      key={record.id}
                      className="border-border/50 hover:bg-muted/50"
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedRecords.includes(record.id)}
                          onCheckedChange={() => handleSelectRecord(record.id)}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {record.profiles 
                          ? `${record.profiles.first_name} ${record.profiles.last_name}`
                          : record.user_id.slice(0, 8) + '...'
                        }
                      </TableCell>
                      <TableCell>
                        <Badge className={getTypeColor(record.type)}>
                          {getTypeLabel(record.type)}
                        </Badge>
                      </TableCell>
                      <TableCell className={`${record.amount < 0 ? 'text-destructive' : 'text-primary'}`}>
                        {formatAmount(record.amount)}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-xs truncate">
                        {record.description}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {record.phases?.name && (
                          <div className="text-sm">
                            {record.phases.name}
                            {record.groups?.name && (
                              <div className="text-muted-foreground">{record.groups.name}</div>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(record.status)}
                          <span className="text-sm text-muted-foreground">
                            {getStatusLabel(record.status)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatArmenianDateTime(record.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {record.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => updateRecordStatus(record.id, 'approved')}
                                className="bg-primary hover:bg-primary/90 px-2"
                              >
                                <Check className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => updateRecordStatus(record.id, 'rejected')}
                                className="px-2"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteRecord(record.id)}
                            className="border-destructive text-destructive hover:bg-destructive/90 hover:text-primary-foreground px-2"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};